import { type BlockStyle, Chevron, Modal, RichEditor } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { DatePicker, Divider, Input, Select } from 'antd'
import { hasMeaningfulStoredHtml } from 'common/utils/html'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  type EolFieldConfig,
  type EolSectionConfigItem,
  eolSectionConfig
} from './eolSectionConfig'
import type { EolEntry, EolStructuredSection } from './eolTypes'

const { TextArea } = Input

const CARE_PLAN_BLOCK_STYLES: BlockStyle[] = [
  'paragraph',
  'bulletList',
  'orderedList'
]

/** Matches the save path: fully cleared rows are dropped and not persisted. */
const isNonEmptyEolEntry = (entry: EolEntry): boolean =>
  Object.entries(entry).some(([key, v]) => {
    if (!v || v.trim() === '') return false
    // HTML values (e.g. descriptionHtml) must contain meaningful content beyond empty paragraphs
    if (key.endsWith('Html')) return hasMeaningfulStoredHtml(v)
    return true
  })

const hasMissingRequiredValues = (
  entries: EolEntry[],
  fields: EolFieldConfig[]
): boolean =>
  entries.some((entry) =>
    fields.some((field) => {
      if (field.type === 'richtext') {
        const htmlVal = entry[`${field.key}Html`]
        // Only treat the HTML value as authoritative when it has meaningful content.
        // Placeholder HTML (<p>&nbsp;</p>, <p></p>) falls back to the plain key.
        if (htmlVal && hasMeaningfulStoredHtml(htmlVal)) return false
        const plainVal = entry[field.key]
        return !plainVal || plainVal.trim() === ''
      }
      const value = entry[field.key]
      return !value || value.trim() === ''
    })
  )

const todayIsoDate = (): string => dayjs().format('YYYY-MM-DD')

/**
 * Prefill `observationDate` with today when empty so new/edited rows have a
 * valid `YYYY-MM-DD` value. Other dates (e.g. `awarenessDate`) are not
 * auto-filled — the user must choose them; the API still requires every field
 * defined on that section’s entry schema.
 */
const withObservationDateDefaults = (
  entry: EolEntry,
  fields: EolFieldConfig[]
): EolEntry => {
  let next = { ...entry }
  for (const field of fields) {
    if (field.key !== 'observationDate') continue
    const value = next[field.key]
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      next = { ...next, [field.key]: todayIsoDate() }
    }
  }
  return next
}

interface AddSectionModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  onSave: (section: EolStructuredSection) => void
  existingSections: EolStructuredSection[]
  customSectionFlagEnabled?: boolean
  isEolCarePlan?: boolean
  editingSection?: EolStructuredSection | null
}

const AddSectionModal: React.FC<AddSectionModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  onSave,
  existingSections,
  customSectionFlagEnabled = true,
  isEolCarePlan = false,
  editingSection = null
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  // TODO: Remove custom_careplan_section flag gating once end_of_life_care_plan is enabled for all homes.
  // At that point, all homes will be EOL enabled and this filter can be replaced with just `eolSectionConfig`.
  const availableConfigs = eolSectionConfig.filter((c) => {
    if (c.sectionType === 'custom' && !customSectionFlagEnabled) return false
    if (!isEolCarePlan && c.sectionType !== 'custom') return false
    return true
  })
  const [selectedConfig, setSelectedConfig] =
    useState<EolSectionConfigItem | null>(null)
  const [entries, setEntries] = useState<EolEntry[]>([{}])
  const [customLabel, setCustomLabel] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isModalVisible && editingSection) {
      const config = eolSectionConfig.find(
        (c) => c.sectionType === editingSection.sectionType
      )
      if (config) {
        setSelectedConfig(config)
        setEntries(
          editingSection.entries.length > 0
            ? editingSection.entries.map((e) =>
                withObservationDateDefaults(e, config.fields)
              )
            : [withObservationDateDefaults({}, config.fields)]
        )
        if (editingSection.sectionType === 'custom') {
          setCustomLabel(editingSection.label)
        }
      }
    }
  }, [isModalVisible, editingSection])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!isModalVisible || editingSection) return
    setSelectedConfig(null)
    setEntries([{}])
    setCustomLabel('')
  }, [isModalVisible, editingSection])

  const handleClose = () => {
    setSelectedConfig(null)
    setEntries([{}])
    setCustomLabel('')
    setIsModalVisible(false)
  }

  const handleSelectSectionType = (config: EolSectionConfigItem) => {
    setSelectedConfig(config)
    setEntries([withObservationDateDefaults({}, config.fields)])
    setCustomLabel('')
  }

  const handleBackToTypes = () => {
    if (editingSection) {
      handleClose()
      return
    }
    setSelectedConfig(null)
    setEntries([{}])
  }

  const handleEntryChange = (
    index: number,
    key: string,
    value: string | undefined
  ) => {
    setEntries((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [key]: value }
      return updated
    })
  }

  const handleSave = () => {
    if (!selectedConfig) return

    const nonEmptyEntries = entries.filter(isNonEmptyEolEntry)
    if (nonEmptyEntries.length === 0) return
    if (hasMissingRequiredValues(nonEmptyEntries, selectedConfig.fields)) return

    const isCustom = selectedConfig.sectionType === 'custom'
    const label =
      isCustom && customLabel.trim() ? customLabel.trim() : selectedConfig.label

    onSave({
      sectionType: selectedConfig.sectionType,
      label,
      entries: nonEmptyEntries
    })
    handleClose()
  }

  const isSectionAtMax = (config: EolSectionConfigItem) => {
    if (config.maxSections === Infinity) return false
    const existing = existingSections.filter(
      (s) => s.sectionType === config.sectionType
    )
    const count =
      editingSection?.sectionType === config.sectionType
        ? existing.length - 1
        : existing.length
    return count >= config.maxSections
  }

  const hasValidEntries = entries.some(isNonEmptyEolEntry)
  const nonEmptyEntriesForValidation = entries.filter(isNonEmptyEolEntry)
  const hasAnyMissingRequiredValues =
    !!selectedConfig &&
    hasMissingRequiredValues(
      nonEmptyEntriesForValidation,
      selectedConfig.fields
    )

  const isCustom = selectedConfig?.sectionType === 'custom'
  const isSaveDisabled =
    !hasValidEntries ||
    hasAnyMissingRequiredValues ||
    (isCustom && !customLabel.trim())

  if (!isModalVisible) return null

  const isEditing = !!editingSection

  return (
    <StyledModal
      noPadding
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onCancel={handleClose}
      onConfirm={selectedConfig ? handleSave : undefined}
      closeButton
      closeOnConfirm={false}
      testId="eol-add-section"
      confirmButtonProps={
        selectedConfig
          ? { disabled: isSaveDisabled, variant: 'primary' }
          : undefined
      }
      localisation={{
        title: selectedConfig
          ? selectedConfig.label
          : t(carePlansConstants.EOL_ADD_SECTION),
        confirmText: selectedConfig
          ? isEditing
            ? t(carePlansConstants.EOL_UPDATE_SECTION)
            : t(carePlansConstants.EOL_SAVE_SECTION)
          : '',
        cancelText: selectedConfig
          ? t(carePlansConstants.EOL_CANCEL)
          : undefined
      }}
    >
      {!selectedConfig && (
        <>
          <SubtitleText>
            {t(carePlansConstants.EOL_ADD_SECTION_SUBTITLE)}
          </SubtitleText>
          <Divider style={{ margin: '12px 0' }} />
          <SectionTypeList>
            {availableConfigs.map((config) => {
              const isDisabled = isSectionAtMax(config)
              return (
                <SectionTypeItem
                  key={config.sectionType}
                  onClick={() => !isDisabled && handleSelectSectionType(config)}
                  $disabled={isDisabled}
                  data-testid={`eol-section-type-${config.sectionType}`}
                >
                  <SectionTypeInfo>
                    <SectionTypeLabel weight="medium">
                      {config.label}
                    </SectionTypeLabel>
                    <SectionTypeDescription>
                      {config.description}
                    </SectionTypeDescription>
                  </SectionTypeInfo>
                  <ChevronIcon>
                    <Chevron direction="right" />
                  </ChevronIcon>
                </SectionTypeItem>
              )
            })}
          </SectionTypeList>
        </>
      )}
      {selectedConfig && (
        <>
          {!isEditing && (
            <BackButton
              onClick={handleBackToTypes}
              data-testid="eol-back-to-types-btn"
            >
              <Chevron direction="left" />{' '}
              {t(carePlansConstants.EOL_BACK_TO_SECTION_TYPES)}
            </BackButton>
          )}
          <Divider style={{ margin: '12px 0' }} />
          <SectionHeader>
            <SectionHeaderInfo>
              <SectionTypeLabel weight="medium">
                {selectedConfig.label}
              </SectionTypeLabel>
              <SectionTypeDescription>
                {selectedConfig.description}
              </SectionTypeDescription>
            </SectionHeaderInfo>
          </SectionHeader>
          <EntriesContainer>
            {isCustom && (
              <EntryCard data-testid="eol-custom-title-card">
                <FieldContainer>
                  <FieldLabel weight="medium">
                    {t(carePlansConstants.EOL_SECTION_TITLE_LABEL)}{' '}
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </FieldLabel>
                  <Input
                    placeholder={t(
                      carePlansConstants.EOL_SECTION_TITLE_PLACEHOLDER
                    )}
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    data-testid="eol-custom-title-input"
                  />
                </FieldContainer>
              </EntryCard>
            )}
            {entries.map((entry, entryIndex) => (
              <EntryCard
                key={entryIndex}
                data-testid={`eol-entry-card-${entryIndex}`}
              >
                {selectedConfig.fields.map((field) => (
                  <FieldContainer key={field.key}>
                    <FieldLabel weight="medium">
                      {field.label} <RequiredAsterisk>*</RequiredAsterisk>
                    </FieldLabel>
                    {renderField(field, entry, entryIndex, handleEntryChange)}
                  </FieldContainer>
                ))}
              </EntryCard>
            ))}
          </EntriesContainer>
        </>
      )}
    </StyledModal>
  )
}

const renderField = (
  field: EolFieldConfig,
  entry: EolEntry,
  entryIndex: number,
  onChange: (index: number, key: string, value: string | undefined) => void
) => {
  switch (field.type) {
    case 'richtext': {
      const htmlValue = entry[`${field.key}Html`] ?? entry[field.key] ?? ''
      return (
        <Suspense fallback={<div>Loading...</div>}>
          <RichEditor
            html={htmlValue}
            onChangeHtml={(html) => {
              onChange(entryIndex, `${field.key}Html`, html)
              // Clear stale plain-text sibling so the backend receives consistent data
              onChange(entryIndex, field.key, undefined)
            }}
            blockStyles={CARE_PLAN_BLOCK_STYLES}
          />
        </Suspense>
      )
    }
    case 'textarea':
      return (
        <TextArea
          placeholder="Type here ..."
          autoSize={{ minRows: 3 }}
          value={entry[field.key] || ''}
          onChange={(e) => onChange(entryIndex, field.key, e.target.value)}
        />
      )
    case 'date': {
      const raw = entry[field.key]
      const dateValue =
        raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()
          ? dayjs(raw, 'YYYY-MM-DD', true)
          : undefined
      return (
        <DatePicker
          style={{ width: '100%' }}
          format="DD-MM-YYYY"
          value={dateValue}
          onChange={(date) =>
            onChange(
              entryIndex,
              field.key,
              date ? date.format('YYYY-MM-DD') : undefined
            )
          }
          placeholder="dd-mm-yyyy"
        />
      )
    }
    case 'select': {
      const isSnomed = field.key === 'snomedCode'
      return (
        <Select
          style={{ width: '100%' }}
          placeholder={`Select ${field.label.toLowerCase()}`}
          value={entry[field.key] || undefined}
          onChange={(value) => onChange(entryIndex, field.key, value)}
          data-testid={isSnomed ? 'eol-snomed-select' : undefined}
        >
          {field.options?.map((option, optionIndex) => (
            <Select.Option
              key={option.value}
              value={option.value}
              data-testid={
                isSnomed ? `eol-snomed-option-${optionIndex}` : undefined
              }
            >
              {option.label}
            </Select.Option>
          ))}
        </Select>
      )
    }
    default:
      return null
  }
}

const StyledModal = styled(Modal)`
  min-width: 600px;
  max-width: 700px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

const SubtitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 14px;
  margin-bottom: 0;
`

const SectionTypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
`

const SectionTypeItem = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $disabled, theme }) =>
      $disabled ? 'transparent' : theme.color.neutral.color9};
  }
`

const SectionTypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
`

const SectionTypeLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color2};
  font-size: 16px;
  margin-bottom: 0;
`

const SectionTypeDescription = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 14px;
  margin-bottom: 0;
`

const ChevronIcon = styled.span`
  color: ${({ theme }) => theme.color.neutral.color5};
  margin-left: 8px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.neutral.color4};
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.color.status.primary};
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin-bottom: 16px;
`

const SectionHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const EntriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EntryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 12px;
`

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled(Text)`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color2};
  margin-bottom: 0;
`

const RequiredAsterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
`

export default AddSectionModal
